import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { parseISO, isValid } from 'date-fns';

import { ArrayService } from './array.service';
import { DateService, LogService, ValueService } from '@oa/shared-core';
import { QueryFilter } from './listing-search.service';
import { ValueConversionService } from '@oa/shared-core';

@Injectable()
export class QueryFilterService {
  private activeQueryFilterEditing = false;

  private readonly _serviceName = 'QueryFilterService';
  private query = {} as QueryFilter;

  /**
   *  Backing field for the last current, active query filter
   */
  private _activeQueryFilter: QueryFilter;

  /**
   * A copy of the query filter object that is maintained during editing.
   */
  private _shadowActiveQueryFilter: QueryFilter;

  /**
   * Backing field for the last executed query that returned results.
   */
  private _lastQueryWithResults: QueryFilter;

  constructor(
    private log: LogService,
    private converter: ValueConversionService,
    private dateService: DateService,
    private arrayService: ArrayService,
    private valueHelper: ValueService
  ) {
    // initialize the active query filter with an empty query filter
    this.setActiveQueryFilter(this.createQueryFilter());
  }

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging;
  }

  /**
   * Getter for the last executed query that returned results.
   */
  public get lastQueryWithResults(): QueryFilter {
    return this._lastQueryWithResults;
  }

  /**
   *  Setter for the last executed query that returned results.
   */
  public set lastQueryWithResults(v: QueryFilter) {
    let query = {} as QueryFilter;

    this._lastQueryWithResults = Object.assign(query, v);
  }

  /**
   * Begin editing the active query filter
   */
  public beginEditQueryFilter() {
    if (this.activeQueryFilterEditing) {
      console.warn('QueryFilterEditing: Active QueryFilter is already being edited. Reverting active query filter...');

      if (this._shadowActiveQueryFilter) {
        this.revertQueryFilter();
      }

      this.activeQueryFilterEditing = false;
    }

    if (!this.activeQueryFilterEditing) {
      // make the copy
      this._shadowActiveQueryFilter = Object.assign({}, this.activeQueryFilter);
      this.activeQueryFilterEditing = true;

      if (this.verboseLogging) {
        console.log('QueryFilterEditing: beginEditQueryFilter() Editing started!');
      }
    }
  }

  /**
   * Revert the query filter (copies the shadow copy to activeQueryFilter)
   */
  revertQueryFilter() {
    this._activeQueryFilter = Object.assign({}, this._shadowActiveQueryFilter);
    console.warn('QueryFilterEditing: Active query filter reverted to previous state.');
  }

  /**
   * Stops editing of the active query filter with the option to save.  If save is false, revertQueryFilter is called.
   */
  public stopEditQueryFilter(save: boolean) {
    if (this.verboseLogging) {
      console.log('QueryFilterEditing: stopEditQueryFilter(save):', save);
    }

    if (save && !this.activeQueryFilterEditing) {
      console.warn("QueryFilterEditing: You can't save if you aren't editing!");
      return;
    }

    if (this.activeQueryFilterEditing) {
      if (save) {
        this._shadowActiveQueryFilter = null;
      } else {
        this.revertQueryFilter();
      }

      this.activeQueryFilterEditing = false;
    }
  }

  /**
   * Gets the active query filter
   */
  public get activeQueryFilter(): QueryFilter {
    // console.log(`${this._serviceName} get activeQueryFilter(): `,this._activeQueryFilter);

    /*
    if (this._activeQueryFilter && this._activeQueryFilter.fromDate){
      
    }
    */
    return this._activeQueryFilter;
  }

  /**
   * Sets the active query filter */
  public setActiveQueryFilter(v: QueryFilter) {
    if (this.verboseLogging) {
      console.log(`${this._serviceName} setActiveQueryFilter(): `, v);
    }

    this._activeQueryFilter = v;
  }

  /**
   * Assigns parameter values
   * @param query
   * @param params
   */
  public assignParameterValues(query: QueryFilter, params: Params) {
    if (this.verboseLogging) {
      console.log('assignParameterValues');
    }
    query.adults = params.adults;
    query.minAcres = params.minAcres;
    query.maxAcres = params.maxAcres;
    query.geohash = params.geohash;
    query.pageNumber = params.pageNumber;
    query.pageSize = params.pageSize;
    query.sortCol = params.sortCol;
    query.sortDir = params.sortDir;
    query.latitude = params.latitude;
    query.longitude = params.longitude;
    query.distance = params.distance;
    query.fromDate = params.fromDate;
    query.toDate = params.toDate;
    query.showFavorites = this.converter.getDefinedBoolean(params.showFavorites);
    query.featured = params.featured;
    query.locationName = params.locationName;
    query.bypassCache = this.converter.getDefinedBoolean(params[this.valueHelper.findKey(params, 'bypassCache')]);
    query.activities = params.activities;
    query.text = params.text;
    query.searchText = params.searchText;
    query.showPermits = this.converter.getDefinedBoolean(params.showPermits);
    query.showExtendedRentals = this.converter.getDefinedBoolean(params.showExtendedRentals);
    query.showSeasonalRentals = this.converter.getDefinedBoolean(params.showSeasonalRentals);

    // Long term rentals is a search on showPermits,showExtendedRentalsshowSeasonalRentals as an OR
    query.showLongTermRentals = this.converter.getDefinedBoolean(params.showLongTermRentals);

    // Short term rentals is a search that excludes showPermits,showExtendedRentalsshowSeasonalRentals as an OR
    query.showShortTermRentals = this.converter.getDefinedBoolean(params.showShortTermRentals);

    query.zipCode =
      this.arrayService.getValue(params, 'zip') ||
      this.arrayService.getValue(params, 'zipcode') ||
      this.arrayService.getValue(params, 'zip5');

    query.availableToday = this.converter.getDefinedBoolean(params.availableToday);
    query.timeZoneOffset = params.timeZoneOffset;

    return query;
  }

  get activities() {
    return this.get('activities');
  }
  set activities(value: string) {
    this.set('activities', value);
  }

  get adults() {
    return this.getNumber('adults');
  }
  set adults(value: number) {
    this.set('adults', value);
  }

  get children() {
    return this.getNumber('children');
  }
  set children(value: number) {
    this.set('children', value);
  }

  get bypassCache() {
    return this.getBoolean('bypassCache');
  }

  set bypassCache(value: boolean) {
    this.set('bypassCache', value);
  }

  get distance() {
    return this.getNumber('distance');
  }
  set distance(value: number) {
    this.set('distance', value);
  }

  get fromDate() {
    return this.getDate('fromDate');
  }

  set fromDate(value: Date) {
    this.set('fromDate', value);
  }

  get latitude() {
    return this.getNumber('latitude');
  }
  set latitude(value: number) {
    this.set('latitude', value);
  }

  get longitude() {
    return this.getNumber('longitude');
  }
  set longitude(value: number) {
    this.set('longitude', value);
  }

  get locationName() {
    return this.get('locationName');
  }
  set locationName(value: string) {
    this.set('locationName', value);
  }

  get toDate() {
    return this.getDate('toDate');
  }
  set toDate(value: Date) {
    this.set('toDate', value);
  }

  /**
   * Clears the active query filter (by creating a new, empty query filter object)
   */
  clear() {
    this.setActiveQueryFilter(this.createQueryFilter());
  }

  getQueryFilter(): QueryFilter {
    let cleanedActivities: string = '';

    if (!this.activities) {
      this.activities = '';
    }

    if (this.activities.indexOf(',,') == -1) {
      cleanedActivities = this.activities;
    }

    // console.log(`return from ${this._serviceName} getQueryFilter()`);

    return {
      activities: cleanedActivities,
      adults: this.adults,
      children: this.children,
      distance: this.distance,
      fromDate: this.fromDate,
      latitude: this.latitude,
      locationName: this.locationName,
      longitude: this.longitude,
      toDate: this.toDate,
      bypassCache: this.bypassCache,
    } as QueryFilter;
  }

  /**
   * Creates an empty query filter object
   * with default values.
   */
  public createQueryFilter() {
    let q = {} as QueryFilter;

    // q.searchText = "";
    // q.children = 0;
    // q.adults = 1;

    // Note: date pickers expect undefined for unselected date values. Don't use null.
    // q.fromDate = undefined;
    // q.toDate = undefined;

    // q.maxAcres = -1;
    // q.minAcres = -1;

    // q.showExtendedRentals = false;
    // q.showFavorites = false;
    // q.showPermits = false;
    // q.showSeasonalRentals = false;
    // q.activities = "";
    // q.searchText = "";

    // q.showLongTermRentals = false;
    // q.showShortTermRentals = false;

    // q.bypassCache = false;

    // q.pageNumber = 0;

    return q;
  }

  /**
   * Combines or merges 2 query filter objects into 1
   * @param query1
   * @param query2
   */
  combineQueryFilter(query1: QueryFilter, query2: QueryFilter): QueryFilter {
    let query = {} as QueryFilter;

    query = Object.assign(query, query2);

    query = Object.assign(query, query1);

    // query.locationName = query2.locationName;
    // query.longitude = query2.longitude;
    // query.latitude = query2.latitude;
    // query.distance = query2.distance;
    // query.geohash = query2.geohash;
    // query.text = query2.text;

    return query;
  }

  /**
   * Counts the number of filter changes between 2 query filter objects.
   * Used primarily to display filter counts in the UI
   * @param qf1
   * @param qf2
   */
  public getFilterGroupChangeCounts(qf1: QueryFilter, qf2: QueryFilter) {
    // console.log(`${this._serviceName} getFilterGroupChangeCounts`);

    let logMessage = '';

    if (qf1 && qf2) {
      // Assess the change for the search filter group: search text and acreage range
      let filterGroupCount = 0;

      let s1 = qf1.searchText;
      let s2 = qf2.searchText;

      if (this.converter.isEmpty(s1) != this.converter.isEmpty(s2)) {
        filterGroupCount++;
        logMessage = `${this._serviceName} getFilterGroupChangeCounts filterGroupCount increased to ${filterGroupCount}: searchText`;
        // console.log(logMessage);
      } else {
        if (s1 && s2) {
          if (s1 != s2) {
            filterGroupCount++;
            logMessage = `${this._serviceName} getFilterGroupChangeCounts filterGroupCount increased to ${filterGroupCount}: searchText`;
            // console.log(logMessage);
          }
        }
      }

      if (qf1.minAcres && qf2.minAcres && qf1.maxAcres && qf2.maxAcres) {
        if (qf1.minAcres != qf2.minAcres || qf1.maxAcres != qf2.maxAcres) {
          filterGroupCount++;

          logMessage = `${this._serviceName} getFilterGroupChangeCounts filterGroupCount increased to ${filterGroupCount}: acreage`;
          // console.log(logMessage);
        }
      }

      // 
      let searchFilterGroupResult = {
        groupName: 'searchFilterGroup',
        changeCount: filterGroupCount,
      };

      // Assess the change for the other filter group: favorites, permits, seasonal rentals
      let otherGroupCount = 0;

      let b1 = false;
      let b2 = false;

      b1 = this.converter.getDefinedBoolean(qf1.showFavorites);
      b2 = this.converter.getDefinedBoolean(qf2.showFavorites);

      if (b1 != b2) {
        otherGroupCount++;
        logMessage = `${this._serviceName} getFilterGroupChangeCounts otherGroupCount increased to ${otherGroupCount}: showFavorites`;
        // console.log(logMessage);
      }

      b1 = this.converter.getDefinedBoolean(qf1.showPermits);
      b2 = this.converter.getDefinedBoolean(qf2.showPermits);

      if (b1 != b2) {
        otherGroupCount++;
        logMessage = `${this._serviceName} getFilterGroupChangeCounts otherGroupCount increased to ${otherGroupCount}: showPermits`;
        // console.log(logMessage);
      }

      b1 = this.converter.getDefinedBoolean(qf1.showExtendedRentals);
      b2 = this.converter.getDefinedBoolean(qf2.showExtendedRentals);

      if (b1 != b2) {
        otherGroupCount++;
        logMessage = `${this._serviceName} getFilterGroupChangeCounts otherGroupCount increased to ${otherGroupCount}: showExtendedRentals`;
        // console.log(logMessage);
      }

      b1 = this.converter.getDefinedBoolean(qf1.showSeasonalRentals);
      b2 = this.converter.getDefinedBoolean(qf2.showSeasonalRentals);

      if (b1 != b2) {
        otherGroupCount++;
        logMessage = `${this._serviceName} getFilterGroupChangeCounts otherGroupCount increased to ${otherGroupCount}: showSeasonalRentals`;
        // console.log(logMessage);
      }

      let v1 = this.converter.getDefinedNumber(qf1.adults);
      let v2 = this.converter.getDefinedNumber(qf2.adults);

      if (v1 != v2) {
        // 
        otherGroupCount++;
        logMessage = `${this._serviceName} getFilterGroupChangeCounts otherGroupCount increased to ${otherGroupCount}: adults`;
        // console.log(logMessage);
      }

      v1 = this.converter.getDefinedNumber(qf1.children);
      v2 = this.converter.getDefinedNumber(qf2.children);

      if (v1 != v2) {
        otherGroupCount++;
        logMessage = `${this._serviceName} getFilterGroupChangeCounts otherGroupCount increased to ${otherGroupCount}: children`;
        // console.log(logMessage);
      }

      let otherFilterGroupResult = {
        groupName: 'otherFilterGroup',
        changeCount: otherGroupCount,
      };

      // Assess the change for the activity filter group

      // TODO: ideally, would like a count for each activity selected.  But for now, just going to compare
      // the activities string values.

      let activityGroupCount = 0;

      s1 = qf1.activities;
      s2 = qf2.activities;

      let count1 = this.arrayService.countCSV(s1);
      let count2 = this.arrayService.countCSV(s2);

      // console.log('count1=',count1);
      // console.log('count2=',count2);

      if (count1 !== count2) {
        if (this.converter.isEmpty(s1) != this.converter.isEmpty(s2)) {
          activityGroupCount++;
          logMessage = `${this._serviceName} getFilterGroupChangeCounts activityGroupCount increased to ${activityGroupCount}: activities`;
          // console.log(logMessage);

          // console.log('s1=',s1);
          // console.log('s2=',s2);
        } else {
          if (s1 && s2) {
            if (s1 != s2) {
              activityGroupCount++;
              logMessage = `${this._serviceName} getFilterGroupChangeCounts activityGroupCount increased to ${activityGroupCount}: activities`;
              // console.log(logMessage);

              // console.log('s1=',s1);
              // console.log('s2=',s2);
            }
          }
        }
      }

      let activityFilterGroupResult = {
        groupName: 'activityFilterGroup',
        changeCount: activityGroupCount,
      };

      // Combine the counts into an array
      let cnts = [searchFilterGroupResult, otherFilterGroupResult, activityFilterGroupResult];

      return cnts;
    } else {
      return [];
    }
  }

  /**
   * Gets a total of change counts from a collection of named change count objects
   * @param cnts
   */
  getTotalChangeCount(cnts: { groupName: string; changeCount: number }[]) {
    let c = 0;

    if (cnts) {
      cnts.forEach((g) => {
        if (g && g.changeCount) {
          c = c + g.changeCount;
        }
      });
    }

    return c;
  }

  private get(key: string) {
    return localStorage.getItem(key) !== null && localStorage.getItem(key) !== 'undefined' ? localStorage.getItem(key) : undefined;
  }

  private getDate(key: string): Date {
    if (localStorage.getItem(key) !== null) {
      const value = parseISO(localStorage.getItem(key));
      return isValid(value) ? value : undefined;
    }
    return undefined;
  }

  private getNumber(key: string): number {
    if (localStorage.getItem(key) !== null) {
      let value = +localStorage.getItem(key);
      return !isNaN(value) ? value : undefined;
    }
  }

  private getBoolean(key: string): boolean {
    if (localStorage.getItem(key) !== null) {
      return this.converter.getBoolean(localStorage.getItem(key));
    }

    return false;
  }

  private set(key, value) {
    localStorage.setItem(key, value);
  }
}
